// Catalog screen (admin console screen 2 of 5, route /products) — hooks for
// GET /admin/catalog/products (+ categories) and the product/price-tier
// admin mutations. Same TanStack Query + apiClient pattern as every other
// admin hook file (see useAdminRequests.js) — merge-then-invalidate on every
// mutation for instant feedback, then a real refetch to reconcile.
//
// Cost boundary (root CLAUDE.md rule #1 / this task's own scope note): the
// list read below only ever selects id/sku/name/name_am/brand/model/spec/
// unit/categoryId/stockQty/isActive/tiers. The API response ALSO carries
// cost_price/supplier_name/cost_updated_at (adminCatalogRoutes.js's own
// comment: "admin is allowed to see cost data" — that endpoint is shared
// with the separate, not-yet-built Cost management screen). Those three
// fields are deliberately dropped by groupCatalogRows() below and never
// touched anywhere else in this file — Cost management is screen 3, a
// distinct component, not a conditional branch of this one.
//
// Unlike useAdminRequests()'s single, unparameterized query key, this list
// is parameterized by categoryId/search (a search box + category filter,
// same shape as the sales-facing useCatalogProducts()). There is no one
// cache entry a mutation can merge into — queryClient.setQueriesData(...)
// (plural: matches every cached filter variant by key PREFIX) is v5's tool
// for that, used below instead of setQueryData's single-entry form.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import apiClient from 'shared/api/client.js';

const PRODUCTS_KEY_PREFIX = ['admin', 'catalogProducts'];
const CATEGORIES_KEY = ['admin', 'catalogCategories'];
const BRANDS_KEY_PREFIX = ['admin', 'catalogProductBrands'];

const productsKey = (categoryId, search, includeInactive) => [
  ...PRODUCTS_KEY_PREFIX,
  categoryId ?? null,
  search ?? null,
  Boolean(includeInactive),
];

// One row per (product, tier) pair, same flat shape as
// utils/groupProductRows.js's sales-facing equivalent — grouped here
// instead of imported from there because this admin read carries extra
// admin-only columns that groupProductRowsById() was never written to see
// (and, per this file's header comment, must go on being blind to: cost
// fields are the reason this is its own function, not a shared one).
function groupCatalogRows(rows) {
  const map = new Map();
  for (const row of rows) {
    if (!map.has(row.id)) {
      map.set(row.id, {
        id: row.id,
        sku: row.sku,
        name: row.name,
        nameAm: row.name_am,
        brand: row.brand,
        model: row.model,
        spec: row.spec,
        unit: row.unit,
        categoryId: row.category_id,
        stockQty: row.stock_qty,
        // Added by this task's catalog-restructure follow-up (none of these
        // are cost fields — condition/featured/display-description live on
        // `products` itself, verified against
        // backend/migrations/007_catalog_restructure.sql and this file's own
        // header comment, which still holds: cost_price/supplier_name/
        // cost_updated_at are dropped here and nowhere else in this file
        // ever reads them).
        condition: row.condition,
        isFeatured: !!row.is_featured,
        featuredOrder: row.featured_order,
        displayDescription: row.display_description,
        // Multi-select bulk-actions task: only meaningful once includeInactive
        // is used to also fetch deactivated rows (see useAdminCatalogProducts
        // below) — a plain active-only fetch never returns a row with this
        // false, so existing callers that never look at it are unaffected.
        isActive: !!row.is_active,
        tiers: [],
      });
    }
    if (row.tier) {
      map.get(row.id).tiers.push({
        tier: row.tier,
        listPrice: row.list_price,
        floorPrice: row.floor_price,
      });
    }
  }
  return Array.from(map.values());
}

// GET /admin/catalog/products?categoryId=&search=&includeInactive= —
// active-only by default (productRepository.buildListFilter's own
// p.is_active = 1). Multi-select bulk-actions task: `includeInactive: true`
// (CatalogPage now always passes this — see that page's own comment) also
// returns deactivated rows, dimmed rather than hidden, the same "the admin
// can see and bulk-reactivate them" stance UsersPage.jsx already took for
// staff accounts.
export function useAdminCatalogProducts({ categoryId, search, includeInactive } = {}) {
  return useQuery({
    queryKey: productsKey(categoryId, search, includeInactive),
    queryFn: () =>
      apiClient.get('/admin/catalog/products', {
        categoryId,
        search,
        includeInactive: includeInactive ? '1' : undefined,
      }),
    select: (data) => groupCatalogRows(data.products),
  });
}

export function useAdminCategories() {
  return useQuery({
    queryKey: CATEGORIES_KEY,
    queryFn: () => apiClient.get('/admin/catalog/categories'),
    select: (data) => data.categories,
  });
}

// GET /admin/catalog/brands — the flat, admin-managed brand-name list
// (migration 027/028, table product_brands) backing ProductForm's brand
// <select> and CategoriesPage's own "Product brands" management section.
// Active-only by default (same convention as useAdminCatalogProducts'
// includeInactive) — ProductForm wants active suggestions only, while the
// management section passes `includeInactive: true` to also see (and
// reactivate-by-deactivating-again... no, just see, dimmed) deactivated
// brands. products.brand itself stays free-text with no FK either
// direction — this list is a suggestion/consistency aid only, never
// validated against server-side.
function brandsKey(includeInactive) {
  return [...BRANDS_KEY_PREFIX, Boolean(includeInactive)];
}

export function useProductBrands({ includeInactive } = {}) {
  return useQuery({
    queryKey: brandsKey(includeInactive),
    queryFn: () =>
      apiClient.get('/admin/catalog/brands', {
        includeInactive: includeInactive ? '1' : undefined,
      }),
    select: (data) => data.brands,
  });
}

// POST /admin/catalog/brands — { name }. A newly created brand is active by
// construction, so it belongs in every currently-cached variant (active-only
// AND include-inactive) — appended directly via the plural, prefix-matching
// setQueriesData (same tool updateAllProductCaches below uses) rather than
// invalidated, so ProductForm's inline "add new brand" flow can select it
// immediately without a refetch round-trip.
export function useCreateProductBrand() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body) => apiClient.post('/admin/catalog/brands', body),
    onSuccess: (data) => {
      const brand = data.brand;
      queryClient.setQueriesData({ queryKey: BRANDS_KEY_PREFIX }, (prev) => {
        if (!prev || !prev.brands) return prev;
        if (prev.brands.some((b) => b.id === brand.id)) return prev;
        return {
          ...prev,
          brands: [...prev.brands, brand].sort((a, b) => a.name.localeCompare(b.name)),
        };
      });
    },
  });
}

// POST /admin/catalog/brands/:id/deactivate — soft delete, same
// deactivate-not-delete convention as every other catalog entity. Merges the
// returned row into every cached variant in place (so the include-inactive
// management list keeps showing it, dimmed) then invalidates, whose refetch
// is what actually drops it from the active-only variant ProductForm reads
// (this merge can't safely know which variant that is, same reasoning as
// updateAllProductCaches's own callers below).
export function useDeactivateProductBrand() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => apiClient.post(`/admin/catalog/brands/${id}/deactivate`),
    onSuccess: (data) => {
      const brand = data.brand;
      queryClient.setQueriesData({ queryKey: BRANDS_KEY_PREFIX }, (prev) => {
        if (!prev || !prev.brands) return prev;
        return { ...prev, brands: prev.brands.map((b) => (b.id === brand.id ? brand : b)) };
      });
      queryClient.invalidateQueries({ queryKey: BRANDS_KEY_PREFIX });
    },
  });
}

// GET /admin/catalog/categories/:id/effective-attributes — the INHERITED
// attribute set for one category (a Laptops product gets Laptops' own
// attributes plus Computers' shared ones, already merged/de-duplicated
// server-side — catalogService.getEffectiveAttributesForCategory's own
// comment). Disabled (no request at all) when categoryId is falsy: "no
// category selected" renders zero attribute fields, same as a category with
// its own empty set, without needing a real id to call the route with (this
// task's own brief).
export function useEffectiveAttributes(categoryId) {
  return useQuery({
    queryKey: ['admin', 'categoryEffectiveAttributes', categoryId ?? null],
    queryFn: () => apiClient.get(`/admin/catalog/categories/${categoryId}/effective-attributes`),
    select: (data) => data.attributes,
    enabled: Boolean(categoryId),
  });
}

// GET /admin/catalog/products/:id — single-product admin detail. The list
// read above deliberately doesn't carry `attributes` (keeping that query
// flat/cheap), so ProductForm calls this once, when an edit form opens, to
// prefill attribute values. The response also carries a `cost` object
// (admin-cost data) — deliberately NEVER destructured or read anywhere in
// this hook or its caller; only `.attributes` is ever pulled out of this
// response (root CLAUDE.md rule #1 / this task's own scope note).
export function useAdminProductDetail(productId) {
  return useQuery({
    queryKey: ['admin', 'catalogProductDetail', productId ?? null],
    queryFn: () => apiClient.get(`/admin/catalog/products/${productId}`),
    select: (data) => data.product,
    enabled: Boolean(productId),
  });
}

// Applies `updater` to the raw (pre-select, flat-row) cache of every
// currently-mounted products query, regardless of its categoryId/search
// filter — the plural, prefix-matching form of setQueryData (v5).
function updateAllProductCaches(queryClient, updater) {
  queryClient.setQueriesData({ queryKey: PRODUCTS_KEY_PREFIX }, (prev) => {
    if (!prev || !prev.products) return prev;
    return { ...prev, products: updater(prev.products) };
  });
}

function invalidateProducts(queryClient) {
  queryClient.invalidateQueries({ queryKey: PRODUCTS_KEY_PREFIX });
}

// POST /admin/catalog/products — sku is required and immutable from here on
// (ProductForm never renders a sku field once a product exists). A brand
// new product has no price tier yet, so it isn't optimistically merged into
// any cache's row list here (which of the filter variants it belongs
// under — categoryId match, FULLTEXT search match — isn't something this
// hook can safely re-derive client-side); invalidating is enough to bring
// it in on refetch, same "let the server be the source of truth for filter
// membership" call this file makes for the search-filtered case throughout.
export function useCreateProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (body) => apiClient.post('/admin/catalog/products', body),
    onSuccess: () => {
      invalidateProducts(queryClient);
    },
  });
}

// PATCH /admin/catalog/products/:id — partial update of the product row
// itself (not pricing). Merges the returned fields onto every row sharing
// this product id (one row per tier) in every cached filter variant, then
// invalidates to reconcile (e.g. a categoryId change moving the row in/out
// of a category-filtered cache — the merge can't know that, the refetch
// fixes it).
export function useUpdateProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }) => apiClient.patch(`/admin/catalog/products/${id}`, body),
    onSuccess: (data) => {
      const p = data.product;
      updateAllProductCaches(queryClient, (rows) =>
        rows.map((row) =>
          row.id === p.id
            ? {
                ...row,
                sku: p.sku,
                name: p.name,
                name_am: p.name_am,
                brand: p.brand,
                model: p.model,
                spec: p.spec,
                unit: p.unit,
                category_id: p.category_id,
                stock_qty: p.stock_qty,
                condition: p.condition,
                is_featured: p.is_featured,
                featured_order: p.featured_order,
                display_description: p.display_description,
              }
            : row
        )
      );
      invalidateProducts(queryClient);
    },
  });
}

// POST /admin/catalog/products/:id/duplicate — no body; the duplicate's SKU
// is always server-generated (same as useCreateProduct()), and everything
// else (attributes included) is copied server-side; the duplicate has no
// price tiers and is never featured (catalogService.duplicateProduct's own
// comment). Not optimistically merged for the same reason
// useCreateProduct() isn't: which cached filter variant the new row belongs
// under isn't something this hook can safely re-derive client-side —
// invalidate and let the server be the source of truth on refetch.
export function useDuplicateProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id }) => apiClient.post(`/admin/catalog/products/${id}/duplicate`),
    onSuccess: () => {
      invalidateProducts(queryClient);
    },
  });
}

// PUT /admin/catalog/products/:id/feature — the one code path allowed to
// change is_featured/featured_order (catalogService.setProductFeatured's own
// comment). Merges the two fields onto every row sharing this product id in
// every cached filter variant, then invalidates to reconcile.
export function useSetProductFeatured() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, isFeatured, featuredOrder }) =>
      apiClient.put(`/admin/catalog/products/${id}/feature`, { isFeatured, featuredOrder }),
    onSuccess: (data) => {
      const p = data.product;
      updateAllProductCaches(queryClient, (rows) =>
        rows.map((row) =>
          row.id === p.id
            ? { ...row, is_featured: p.is_featured, featured_order: p.featured_order }
            : row
        )
      );
      invalidateProducts(queryClient);
    },
  });
}

// POST /admin/catalog/products/:id/deactivate — soft delete. Flips
// is_active=0 in place on every cached filter variant rather than removing
// the row: an includeInactive=true variant (CatalogPage's own list) should
// keep showing it, dimmed, exactly like UsersPage.jsx already does for a
// deactivated staff account — only a hypothetical includeInactive=false
// variant would actually need it gone, and invalidateProducts()'s refetch
// handles that correctly per-variant (each one re-applies its own
// includeInactive filter server-side).
export function useDeactivateProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => apiClient.post(`/admin/catalog/products/${id}/deactivate`),
    onSuccess: (_data, id) => {
      updateAllProductCaches(queryClient, (rows) =>
        rows.map((row) => (row.id === id ? { ...row, is_active: 0 } : row))
      );
      invalidateProducts(queryClient);
    },
  });
}

// POST /admin/catalog/products/:id/reactivate — symmetric with deactivate
// above.
export function useReactivateProduct() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id) => apiClient.post(`/admin/catalog/products/${id}/reactivate`),
    onSuccess: (_data, id) => {
      updateAllProductCaches(queryClient, (rows) =>
        rows.map((row) => (row.id === id ? { ...row, is_active: 1 } : row))
      );
      invalidateProducts(queryClient);
    },
  });
}

// --- Bulk actions (multi-select task) -------------------------------------
// No optimistic cache merge — a bulk result is a mixed ok/skipped list, not
// a single updated row; invalidate-and-refetch is simpler and correct.

export function useBulkDeactivateProducts() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids) => apiClient.post('/admin/catalog/products/bulk-deactivate', { ids }),
    onSuccess: () => invalidateProducts(queryClient),
  });
}

export function useBulkReactivateProducts() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (ids) => apiClient.post('/admin/catalog/products/bulk-reactivate', { ids }),
    onSuccess: () => invalidateProducts(queryClient),
  });
}

export function useBulkDeleteProducts() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ ids, confirmedCount }) =>
      apiClient.post('/admin/catalog/products/bulk-delete', { ids, confirmedCount }),
    onSuccess: () => invalidateProducts(queryClient),
  });
}

// GET /admin/catalog/products/bulk-delete-preview?ids=1,2,3 — called
// imperatively right before the confirm dialog opens, see
// utils/bulkActions.js#confirmAndBulkDelete.
export function fetchProductBulkDeletePreview(ids) {
  return apiClient.get('/admin/catalog/products/bulk-delete-preview', { ids: ids.join(',') });
}

// POST /admin/catalog/products/:id/image — multipart product-photo upload
// (ProductForm.jsx's edit-mode-only photo section). apiClient.post accepts a
// FormData body unchanged now (see shared/api/client.js's own comment on
// that branch) so this needs no raw fetch of its own. The response's
// `product` row carries the same raw/snake_case shape as
// useAdminProductDetail's cache (both come from productRepository.findById),
// so on success this merges just image_url onto that one cached detail
// entry — the list caches never render a thumbnail (CatalogPage.jsx's table
// has no image column) and its own list query doesn't even select
// image_url, so there is nothing to update there.
export function useUploadProductImage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, file }) => {
      const formData = new FormData();
      formData.append('image', file);
      return apiClient.post(`/admin/catalog/products/${id}/image`, formData);
    },
    onSuccess: (data, variables) => {
      const p = data.product;
      queryClient.setQueryData(['admin', 'catalogProductDetail', variables.id], (prev) =>
        prev ? { ...prev, product: { ...prev.product, image_url: p.image_url } } : prev
      );
    },
  });
}

// POST /admin/catalog/brands/:id/image — multipart brand-photo upload
// (CategoriesPage.jsx's per-row brand upload control). Same FormData/
// apiClient.post shape as useUploadProductImage() above. The response's
// `brand` row carries the same shape product_brands rows already have in
// the BRANDS_KEY_PREFIX cache, so on success this merges just image_url onto
// that one brand entry in every cached variant (active-only AND
// include-inactive — mirrors useDeactivateProductBrand()'s plural
// setQueriesData above) rather than refetching the whole list.
export function useUploadBrandImage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, file }) => {
      const formData = new FormData();
      formData.append('image', file);
      return apiClient.post(`/admin/catalog/brands/${id}/image`, formData);
    },
    onSuccess: (data) => {
      const b = data.brand;
      queryClient.setQueriesData({ queryKey: BRANDS_KEY_PREFIX }, (prev) => {
        if (!prev || !prev.brands) return prev;
        return {
          ...prev,
          brands: prev.brands.map((brand) =>
            brand.id === b.id ? { ...brand, image_url: b.image_url } : brand
          ),
        };
      });
    },
  });
}

// PUT /admin/catalog/products/:id/prices/:tier — catalogService.upsertPriceTier
// returns { productId, tier, listPrice, floorPrice } (camelCase, built in
// the service, unlike the product-row reads above which are raw snake_case
// DB columns — see that function's own return statement). Merges into the
// matching (id, tier) row if one exists; otherwise appends a new tier row
// for this product (its first-ever price on this tier) and drops that
// product's tier=null placeholder row, if any (see groupCatalogRows(): a
// product with zero price rows produces exactly one row with tier=null from
// the LEFT JOIN — once a real tier exists that placeholder must go, or the
// product would show one real tier plus one phantom "no tier" entry).
export function useUpsertPriceTier() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ productId, tier, listPrice, floorPrice }) =>
      apiClient.put(`/admin/catalog/products/${productId}/prices/${tier}`, {
        listPrice,
        floorPrice,
      }),
    onSuccess: (data) => {
      const { productId, tier, listPrice, floorPrice } = data.price;
      updateAllProductCaches(queryClient, (rows) => {
        const hasProductRows = rows.some((row) => row.id === productId);
        if (!hasProductRows) return rows;

        const hasTierRow = rows.some((row) => row.id === productId && row.tier === tier);
        if (hasTierRow) {
          return rows.map((row) =>
            row.id === productId && row.tier === tier
              ? { ...row, list_price: listPrice, floor_price: floorPrice }
              : row
          );
        }

        const template = rows.find((row) => row.id === productId);
        const withoutPlaceholder = rows.filter(
          (row) => !(row.id === productId && row.tier === null)
        );
        return [
          ...withoutPlaceholder,
          { ...template, tier, list_price: listPrice, floor_price: floorPrice },
        ];
      });
      invalidateProducts(queryClient);
    },
  });
}

export default useAdminCatalogProducts;
